import { useCalendarsStore, useLabelsStore, useSettingsStore, useThemeStore, useTopicsStore } from "../stores/index.js";

let once: Promise<void> | null = null;

/**
 * Hydrate the small entity stores every view depends on (settings, calendars,
 * topics, labels, theme).
 *
 * This runs BEFORE React mounts, not from a mount effect, because the view
 * filters are derived from `calendars` (+ the settings-derived matrix context):
 * landing them late gives the expensive routes a second full render with a new
 * `applyFilters` identity. On the week calendar with a real dataset that second
 * pass re-expanded the window and re-committed the whole grid for no visible
 * change — ~150 ms of a ~750 ms cold start. These tables are tiny; the reads
 * they add before first paint are far cheaper than the render they save.
 *
 * Ordering is a real dependency, not a preference: calendars bootstraps the
 * default-calendar setting, and the theme's first run folds the old block-tint
 * settings + existing entity colors into the theme record — both need settings
 * loaded first. Topics and labels are independent and go in parallel.
 *
 * Runs exactly once per page and never rejects: a failed read must leave the
 * app running on defaults, not white-screen the boot. The resolved promise is
 * KEPT — clearing it on completion would let `Shell`'s safety-net call re-run
 * the whole chain after boot already did, and a second `set({ calendars })`
 * lands a fresh `applyFilters` identity on every view that filters tasks, which
 * is the expensive re-render this exists to remove. Restore/wipe paths
 * re-hydrate the stores directly (`lib/snapshot.ts`), not through here.
 */
export function hydrateAppStores(): Promise<void> {
  once ??= run();
  return once;
}

async function run(): Promise<void> {
  const settle = async (what: string, p: Promise<unknown>) => {
    try {
      await p;
    } catch (e) {
      console.error(`hydrate ${what} failed`, e);
    }
  };
  const independent = Promise.all([
    settle("topics", useTopicsStore.getState().hydrate()),
    settle("labels", useLabelsStore.getState().hydrate()),
  ]);
  await settle("settings", useSettingsStore.getState().hydrate());
  await Promise.all([
    settle("calendars", useCalendarsStore.getState().hydrate()),
    settle("theme", useThemeStore.getState().hydrate()),
    independent,
  ]);
}
